import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { resolvePostLoginPath, userHasActiveSubscription } from '../lib/institutionOnboarding';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, Loader2, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Seo } from '../components/seo/Seo';
import { InlineLoader } from '../components/ui/page-loader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AuthShell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, hsl(220 40% 97%) 0%, hsl(0 0% 100%) 100%)' }}>
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <svg className="absolute inset-0 w-full h-full" style={{ opacity: 0.6 }}>
          <defs>
            <pattern id="auth-dots" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
              <circle cx="16" cy="16" r="1" fill="#0055FF" opacity="0.12" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#auth-dots)" />
        </svg>
      </div>
      <div className="w-full max-w-md relative z-10">
        <div className="flex justify-center mb-10">
          <Link to="/" className="med900-logo text-3xl">
            <span className="med900-logo-med">Med</span>
            <span className="med900-logo-900">900</span>
            <span className="med900-logo-dot">.</span>
          </Link>
        </div>
        {children}
      </div>
    </div>
  );
}

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || location.state?.from;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userData = await login(email, password);
      let memberships = [];
      try {
        const token = localStorage.getItem('ucat_token');
        const res = await axios.get(`${API_URL}/api/institutions/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        memberships = res.data || [];
      } catch {
        memberships = [];
      }
      navigate(resolvePostLoginPath({
        redirectTo,
        memberships,
        hasActiveSubscription: userHasActiveSubscription(userData),
      }));
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (!err.response) setError('Cannot reach the local API at 127.0.0.1:8000. Start the backend, then try again.');
      else setError('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Log In | Med900"
        description="Log in to Med900 to continue UCAT practice, mocks, analytics, notes, and study planning."
        path="/login"
        robots="noindex, nofollow"
      />
      <AuthShell>
      <div className="bg-card rounded-2xl border border-border p-8 shadow-xl shadow-black/[0.03]">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-cabinet font-black tracking-tight">Welcome back</h1>
          <p className="text-sm text-muted-foreground font-plex mt-1">Sign in to continue your UCAT preparation</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-xl text-sm font-plex" data-testid="login-error">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium font-plex">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="rounded-xl h-11"
              data-testid="login-email"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-sm font-medium font-plex">Password</Label>
              <Link to="/forgot-password" className="text-xs text-primary hover:underline font-plex">
                Forgot password?
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="rounded-xl h-11"
              data-testid="login-password"
            />
          </div>

          <Button type="submit" className="w-full rounded-full h-11 font-cabinet font-semibold text-base" disabled={loading} data-testid="login-submit">
            {loading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in...</>
            ) : (
              <>Sign in <ArrowRight className="ml-2 h-4 w-4" /></>
            )}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm font-plex">
          <span className="text-muted-foreground">Don't have an account? </span>
          <Link to="/signup" className="text-primary hover:underline font-semibold" data-testid="signup-link">
            Sign up
          </Link>
        </div>
      </div>

      <p className="text-center text-xs text-muted-foreground mt-6 font-plex">
        By signing in, you agree to our Terms of Service and Privacy Policy
      </p>
      </AuthShell>
    </>
  );
};

export const SignupPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name);
      navigate('/onboarding');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Sign Up | Med900"
        description="Create a Med900 account to start UCAT preparation with mocks, practice questions, analytics, and study guides."
        path="/signup"
        robots="noindex, nofollow"
      />
      <AuthShell>
      <div className="bg-card rounded-2xl border border-border p-8 shadow-xl shadow-black/[0.03]">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-cabinet font-black tracking-tight">Create your account</h1>
          <p className="text-sm text-muted-foreground font-plex mt-1">Start your UCAT preparation journey today</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-xl text-sm font-plex" data-testid="signup-error">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium font-plex">Full Name</Label>
            <Input id="name" type="text" placeholder="John Doe" value={name}
              onChange={(e) => setName(e.target.value)} required className="rounded-xl h-11" data-testid="signup-name" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium font-plex">Email</Label>
            <Input id="email" type="email" placeholder="you@example.com" value={email}
              onChange={(e) => setEmail(e.target.value)} required className="rounded-xl h-11" data-testid="signup-email" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium font-plex">Password</Label>
            <Input id="password" type="password" placeholder="••••••••" value={password}
              onChange={(e) => setPassword(e.target.value)} required className="rounded-xl h-11" data-testid="signup-password" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-sm font-medium font-plex">Confirm Password</Label>
            <Input id="confirmPassword" type="password" placeholder="••••••••" value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)} required className="rounded-xl h-11" data-testid="signup-confirm-password" />
          </div>

          <Button type="submit" className="w-full rounded-full h-11 font-cabinet font-semibold text-base" disabled={loading} data-testid="signup-submit">
            {loading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating account...</>
            ) : (
              <>Create account <ArrowRight className="ml-2 h-4 w-4" /></>
            )}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm font-plex">
          <span className="text-muted-foreground">Already have an account? </span>
          <Link to="/login" className="text-primary hover:underline font-semibold" data-testid="login-link">
            Sign in
          </Link>
        </div>
      </div>

      <p className="text-center text-xs text-muted-foreground mt-6 font-plex">
        By signing up, you agree to our Terms of Service and Privacy Policy
      </p>
      </AuthShell>
    </>
  );
};

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Forgot Password | Med900"
        description="Reset your Med900 account password."
        path="/forgot-password"
        robots="noindex, nofollow"
      />
      <AuthShell>
        <div className="bg-card rounded-2xl border border-border p-8 shadow-xl shadow-black/[0.03]">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-cabinet font-black tracking-tight">Forgot password</h1>
            <p className="text-sm text-muted-foreground font-plex mt-1">
              Enter your email and we&apos;ll send you a reset link
            </p>
          </div>

          {sent ? (
            <div className="space-y-4 text-center">
              <div className="flex justify-center">
                <CheckCircle2 className="h-12 w-12 text-emerald-500" />
              </div>
              <p className="text-sm font-plex text-muted-foreground">
                If an account exists for that email, a reset link has been sent. Check your inbox.
              </p>
              <Link to="/login" className="text-primary hover:underline text-sm font-semibold font-plex">
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-xl text-sm font-plex">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-sm font-medium font-plex">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="rounded-xl h-11"
                />
              </div>
              <Button type="submit" className="w-full rounded-full h-11 font-cabinet font-semibold" disabled={loading}>
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>
                ) : (
                  'Send reset link'
                )}
              </Button>
              <div className="text-center">
                <Link to="/login" className="text-sm text-primary hover:underline font-plex">
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </AuthShell>
    </>
  );
};

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const validate = async () => {
      if (!token) {
        setValidating(false);
        setTokenValid(false);
        return;
      }
      try {
        const res = await axios.get(`${API_URL}/api/auth/reset-password/validate`, {
          params: { token },
        });
        setTokenValid(Boolean(res.data?.valid));
      } catch {
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Reset Password | Med900"
        description="Set a new password for your Med900 account."
        path="/reset-password"
        robots="noindex, nofollow"
      />
      <AuthShell>
        <div className="bg-card rounded-2xl border border-border p-8 shadow-xl shadow-black/[0.03]">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-cabinet font-black tracking-tight">Reset password</h1>
            <p className="text-sm text-muted-foreground font-plex mt-1">Choose a new password for your account</p>
          </div>

          {validating ? (
            <InlineLoader message="Checking reset link" />
          ) : success ? (
            <div className="space-y-4 text-center">
              <div className="flex justify-center">
                <CheckCircle2 className="h-12 w-12 text-emerald-500" />
              </div>
              <p className="text-sm font-plex text-muted-foreground">
                Your password has been updated. Redirecting to sign in...
              </p>
            </div>
          ) : !tokenValid ? (
            <div className="space-y-4 text-center">
              <div className="flex items-center justify-center gap-2 p-3 bg-destructive/10 text-destructive rounded-xl text-sm font-plex">
                <AlertCircle size={16} />
                This reset link is invalid or has expired.
              </div>
              <Link to="/forgot-password" className="text-primary hover:underline text-sm font-semibold font-plex">
                Request a new link
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-xl text-sm font-plex">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-sm font-medium font-plex">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="rounded-xl h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-new-password" className="text-sm font-medium font-plex">Confirm password</Label>
                <Input
                  id="confirm-new-password"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  className="rounded-xl h-11"
                />
              </div>
              <Button type="submit" className="w-full rounded-full h-11 font-cabinet font-semibold" disabled={loading}>
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Updating...</>
                ) : (
                  'Update password'
                )}
              </Button>
            </form>
          )}
        </div>
      </AuthShell>
    </>
  );
};
